import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, ArrowLeft, Loader2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Section } from "@/components/blocks";
import api from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>;
  if (!post) return <Section><p className="text-center text-slate-500">Article not found. <Link to="/blog" className="text-teal-600 underline">Back to blog</Link></p></Section>;

  return (
    <article data-testid="blog-post-page">
      <div className="relative bg-slate-900">
        <img src={post.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 to-slate-900/90" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <Button asChild variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10 mb-6"><Link to="/blog" data-testid="blogpost-back"><ArrowLeft className="w-4 h-4" /> Back to blog</Link></Button>
          <Badge className="bg-teal-500/20 text-teal-300 hover:bg-teal-500/20 border border-teal-500/30 mb-4">{post.category}</Badge>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">{post.title}</h1>
          <div className="flex items-center gap-4 mt-6 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.read_time} min read</span>
            <span>{new Date(post.date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="font-serif-editorial text-xl text-slate-700 leading-relaxed mb-8">{post.excerpt}</p>
        <div className="prose prose-slate max-w-none space-y-5 text-slate-700 leading-relaxed" data-testid="blogpost-body">
          {post.body ? post.body.map((blk, i) => blk.h2
            ? <h2 key={i} className="font-display text-2xl font-bold text-slate-900 pt-2">{blk.h2}</h2>
            : blk.ul
            ? <ul key={i} className="space-y-3 pl-1">{blk.ul.map((li) => {
                const [lead, ...rest] = li.split(". ");
                return <li key={li} className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" /><span><strong className="text-slate-900">{lead}.</strong> {rest.join(". ")}</span></li>;
              })}</ul>
            : <p key={i}>{blk.p}</p>) : (
          <>
          <p>The journey into teaching is one of the most rewarding — and demanding — career paths there is. Whether you are preparing for your first placement or stepping into your first classroom, the difference between struggling and thriving usually comes down to preparation, reflection and the right support.</p>
          <p>At Teaching Pathways, we've seen firsthand how the right frameworks and mentoring transform confidence. This article shares practical, field-tested strategies you can apply immediately — drawn from years of classroom practice, mentoring pre-service teachers and lecturing the next generation of educators.</p>
          <h2 className="font-display text-2xl font-bold text-slate-900">Start with relationships</h2>
          <p>Everything meaningful in a school is built on relationships — with students, mentors and colleagues. Invest early, listen more than you speak, and approach every interaction with curiosity and humility. The staffroom is as much a part of your learning as the classroom.</p>
          <h2 className="font-display text-2xl font-bold text-slate-900">Reflect deliberately</h2>
          <p>Reflection is not an afterthought; it's the engine of professional growth. Set weekly goals, seek specific feedback, and keep a structured journal aligned to the professional standards. Small, consistent improvements compound into real capability.</p>
          <p>Ready to go deeper? Explore our resources and workshops designed to support you at every stage.</p>
          </>
          )}
        </div>
        {post.author && post.author !== "Teaching Pathways" && (
          <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-start gap-4" data-testid="blogpost-author">
            <span className="w-12 h-12 rounded-full bg-teal-600 text-white font-display font-bold flex items-center justify-center shrink-0">
              {post.author.split(" ").map((p) => p[0]).slice(0, 2).join("")}
            </span>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Written by</p>
              <p className="font-display font-bold text-slate-900 mt-0.5">{post.author}</p>
              {post.author_title && <p className="text-sm text-slate-600">{post.author_title}</p>}
            </div>
          </div>
        )}
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild className="bg-teal-600 hover:bg-teal-700 rounded-full"><Link to="/resources" data-testid="blogpost-resources-cta">Explore resources</Link></Button>
          <Button asChild variant="outline" className="rounded-full"><Link to="/contact" data-testid="blogpost-contact-cta">Get in touch</Link></Button>
        </div>
      </div>
    </article>
  );
}
